import { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoCard from "@/components/base/VideoCard";
import SEOSchema from "@/components/base/SEOSchema";
import { videos } from "@/mocks/videos";
import { articles } from "@/mocks/articles";

export default function ArticlePage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const isVideo = id?.startsWith("vid-") ?? false;
  const isArticle = id?.startsWith("art-") ?? false;

  const video = useMemo(() => {
    if (!isVideo) return null;
    return videos.find((v) => v.id === id) || null;
  }, [id, isVideo]);

  const article = useMemo(() => {
    if (!isArticle) return null;
    return articles.find((a) => a.id === id) || null;
  }, [id, isArticle]);

  const item = video || article;

  const relatedVideos = useMemo(() => {
    if (!item) return [];
    return videos
      .filter((v) => v.id !== item.id && v.categorySlug === item.categorySlug)
      .slice(0, 4);
  }, [item]);

  if ((!isVideo && !isArticle) || !item) {
    return <Navigate to="/" replace />;
  }

  const isVideoContent = !!video;

  function formatViews(views: number): string {
    if (views >= 10000000) return `${(views / 10000000).toFixed(1)}千万`;
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return views.toString();
  }

  return (
    <>
      <SEOSchema
        type="Article"
        data={{
          headline: item.title,
          description: "summary" in item ? (item as typeof articles[0]).summary : (item as typeof videos[0]).description,
          datePublished: item.publishDate,
          author: {
            "@type": "Person",
            name: "author" in item ? (item as typeof articles[0]).author : "17c编辑团队",
          },
          publisher: {
            "@type": "Organization",
            name: "17c com一起草在线",
          },
        }}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="mx-auto max-w-[1400px] px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: t("breadcrumbHome"), href: "/" },
                { label: item.category, href: `/category/${item.categorySlug}` },
                { label: item.title },
              ]}
            />

            <div className="flex flex-col lg:flex-row gap-8 pb-14">
              <article className="flex-1 min-w-0">
                {isVideoContent && video && (
                  <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-foreground-950 mb-6">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      title={video.title}
                      className="w-full h-full object-cover"
                      width="1280"
                      height="720"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center group cursor-pointer">
                      <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-white/90 group-hover:bg-primary-500 group-hover:scale-110 transition-all duration-300">
                        <i className="ri-play-fill text-primary-600 group-hover:text-white text-3xl md:text-4xl ml-1"></i>
                      </div>
                    </div>
                    <span className="absolute bottom-3 right-3 bg-black/75 text-white text-sm px-3 py-1 rounded font-label">
                      {video.duration}
                    </span>
                  </div>
                )}

                {!isVideoContent && article && (
                  <div className="relative w-full aspect-[2/1] rounded-lg overflow-hidden bg-background-200 mb-6">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover"
                      width="1200"
                      height="600"
                    />
                  </div>
                )}

                <h1 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-4">
                  {item.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 md:gap-4 mb-6 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {"author" in item ? (item as typeof articles[0]).author : "17c编辑团队"}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {item.publishDate}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {formatViews(item.views)}{isVideoContent ? "次播放" : "阅读"}
                  </span>
                  <Link
                    to={`/category/${item.categorySlug}`}
                    className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium hover:bg-secondary-200 transition-colors cursor-pointer"
                  >
                    {item.category}
                  </Link>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {item.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-lg p-5 md:p-6 mb-8">
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    {"summary" in item
                      ? (item as typeof articles[0]).summary
                      : (item as typeof videos[0]).description}
                  </p>
                </div>

                {!isVideoContent && article && (
                  <div
                    className="prose prose-sm max-w-none text-foreground-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  />
                )}

                {isVideoContent && video && (
                  <div className="mt-8">
                    <h2 className="text-lg font-semibold text-foreground-900 font-heading mb-4">
                      影片详情
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      <div className="p-4 rounded-lg bg-background-100">
                        <span className="text-xs text-foreground-400">分类</span>
                        <p className="text-sm font-medium text-foreground-800 mt-1">
                          {video.category}
                        </p>
                      </div>
                      <div className="p-4 rounded-lg bg-background-100">
                        <span className="text-xs text-foreground-400">评分</span>
                        <p className="text-sm font-medium text-foreground-800 mt-1">
                          <i className="ri-star-fill text-secondary-500 mr-1"></i>
                          {video.score} / 10
                        </p>
                      </div>
                      <div className="p-4 rounded-lg bg-background-100">
                        <span className="text-xs text-foreground-400">时长</span>
                        <p className="text-sm font-medium text-foreground-800 mt-1">
                          {video.duration}
                        </p>
                      </div>
                      {video.episodes && (
                        <div className="p-4 rounded-lg bg-background-100">
                          <span className="text-xs text-foreground-400">集数</span>
                          <p className="text-sm font-medium text-foreground-800 mt-1">
                            共{video.episodes}集
                          </p>
                        </div>
                      )}
                      <div className="p-4 rounded-lg bg-background-100">
                        <span className="text-xs text-foreground-400">上映日期</span>
                        <p className="text-sm font-medium text-foreground-800 mt-1">
                          {video.publishDate}
                        </p>
                      </div>
                      <div className="p-4 rounded-lg bg-background-100">
                        <span className="text-xs text-foreground-400">播放量</span>
                        <p className="text-sm font-medium text-foreground-800 mt-1">
                          {formatViews(video.views)}次
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </article>

              <aside className="lg:w-[340px] shrink-0">
                <div className="sticky top-20">
                  <h3 className="text-base font-semibold text-foreground-900 font-heading mb-4">
                    相关推荐
                  </h3>
                  <div className="space-y-4">
                    {relatedVideos.map((v) => (
                      <Link
                        key={v.id}
                        to={`/article/${v.id}`}
                        className="flex gap-3 group cursor-pointer"
                      >
                        <div className="w-[140px] shrink-0 aspect-video rounded-md overflow-hidden bg-background-200">
                          <img
                            src={v.thumbnail}
                            alt={v.title}
                            title={v.title}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                            width="280"
                            height="157"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                            {v.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-500">
                            <span>{formatViews(v.views)}次播放</span>
                            <span className="flex items-center gap-0.5">
                              <i className="ri-star-fill text-secondary-500 text-[10px]"></i>
                              {v.score}
                            </span>
                          </div>
                          <span className="inline-block mt-1.5 text-xs px-2 py-0.5 rounded bg-secondary-100 text-secondary-700">
                            {v.category}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}